import React from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue } from './utils';
import EmptySection from './EmptySection';
import { CreditBureauReport as CreditBureau, Maybe } from '@/graphql/__generated__/van-credits';
import { Registry } from '@/graphql/__generated__/onboarding';
import CreditBureauSummary from '../../Onboarding/NaturalPerson/RegistryDetails/CreditBureauSummary';

interface CreditBureauReportProps {
	data: Maybe<CreditBureau> | undefined;
	title: string;
	registry: Registry | null | undefined;
}

const CreditBureauReport: React.FC<CreditBureauReportProps> = ({ data, title, registry }) => {
	const t = useTranslations('VanCreditRequestQuestionnaire');

	const getBureauScoreCardColor = (bureauScore: number | null) => {
		if (!bureauScore) return { backgroundColor: 'rgba(128, 128, 128, 0.3)', color: 'gray' };
		if (bureauScore < 550) {
			return { backgroundColor: 'rgba(255, 37, 26, 0.3)', color: '#FF251A' };
		}
		if (bureauScore < 650 && bureauScore >= 550) {
			return { backgroundColor: 'rgba(255, 167, 26, 0.3)', color: '#FFA71A' };
		}
		if (bureauScore < 700 && bureauScore >= 650) {
			return { backgroundColor: 'rgba(16, 130, 82, 0.3)', color: '#108252' };
		}
		if (bureauScore >= 700) {
			return { backgroundColor: 'rgba(26, 95, 255, 0.3)', color: '#1a5FFF' };
		}
	};
	const bureauScoreCardsStyles = getBureauScoreCardColor(data?.creditBureauScore ?? null);
	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			{data ? (
				<div className="flex flex-row w-full">
					<div className="flex flex-row flex-wrap w-full">
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'reportDate'}
								value={formatValue(data.reportDate, t, 'reportDate')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'firstCreditDate'}
								value={formatValue(data.firstCreditDate, t, 'firstCreditDate')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'totalActiveCreditBalance'}
								value={formatValue(data.totalActiveCreditBalance, t, 'totalActiveCreditBalance')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'totalCreditBalance'}
								value={formatValue(data.totalCreditBalance, t, 'totalCreditBalance')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'totalActiveNumberOfCredits'}
								value={formatValue(
									data.totalActiveNumberOfCredits,
									t,
									'totalActiveNumberOfCredits'
								)}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'totalNumberOfCredits'}
								value={formatValue(data.totalNumberOfCredits, t, 'totalNumberOfCredits')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'oldestDuePayment'}
								value={formatValue(data.oldestDuePayment, t, 'oldestDuePayment')}
							/>
						</div>
					</div>
					<div>
						<div
							className="flex flex-col p-2 rounded-xl text-center ml-10"
							style={bureauScoreCardsStyles}
						>
							<Typography weight="semibold" size="md" color={bureauScoreCardsStyles?.color}>
								{t('creditBureauScore')}: {data.creditBureauScore || t('Pending')}
							</Typography>
						</div>
					</div>
				</div>
			) : (
				<EmptySection t={tForComponent(t)} />
			)}

			{registry && (
				<div className="mt-4 pt-4 border-t border-gray-200">
					<CreditBureauSummary registry={registry} />
				</div>
			)}
		</div>
	);
};

export default CreditBureauReport;
